import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { rsFromTo } from '../../shared/motion/rs-motion';

export interface Tech {
  name: string;
  /** Local asset path, or empty when the mark is drawn inline. */
  logo: string;
  /** Inline marks are used where no local asset exists. */
  inline?: 'flutter';
  /** Accent for the card's bottom rule. */
  tone: 'pink' | 'violet' | 'blue';
}

let registered = false;

@Component({
  selector: 'app-tech',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tech.component.html',
  styleUrls: ['./tech.component.scss'],
})
export class TechComponent implements AfterViewInit, OnDestroy {
  @ViewChild('root') root?: ElementRef<HTMLElement>;
  @ViewChild('head') head?: ElementRef<HTMLElement>;
  @ViewChild('grid') grid?: ElementRef<HTMLElement>;
  @ViewChildren('card', { read: ElementRef })
  cardEls?: QueryList<ElementRef<HTMLElement>>;

  reduced = false;

  /** Logos are the real brand assets already in src/assets/images. */
  readonly items: Tech[] = [
    { name: 'Angular', logo: 'assets/images/angular.png', tone: 'pink' },
    { name: 'React', logo: 'assets/images/react-js.webp', tone: 'blue' },
    { name: 'Node.js', logo: 'assets/images/node-js.webp', tone: 'violet' },
    { name: 'Python', logo: 'assets/images/python.png', tone: 'blue' },
    { name: 'Laravel', logo: 'assets/images/laravel.png', tone: 'pink' },
    { name: 'PHP', logo: 'assets/images/php.png', tone: 'violet' },
    { name: 'MongoDB', logo: 'assets/images/mongo.png', tone: 'violet' },
    { name: 'MySQL', logo: 'assets/images/MySQL.png', tone: 'blue' },
    { name: 'Flutter', logo: '', inline: 'flutter', tone: 'blue' },
    { name: 'Android', logo: 'assets/images/android.png', tone: 'violet' },
    { name: 'iOS', logo: 'assets/images/ios.png', tone: 'pink' },
    { name: 'Figma', logo: 'assets/images/figma.png', tone: 'pink' },
  ];

  private ctx?: gsap.Context;
  private io?: IntersectionObserver;
  /** The endless tweens, so visibility can pause exactly those. */
  private loops: gsap.core.Tween[] = [];
  private cleanups: Array<() => void> = [];
  private isBrowser: boolean;

  constructor(
    private zone: NgZone,
    private host: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  trackTech = (_i: number, t: Tech) => t.name;

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.reduced) return;

    if (!registered) {
      gsap.registerPlugin(ScrollTrigger);
      registered = true;
    }

    this.zone.runOutsideAngular(() => {
      this.ctx = gsap.context(() => {
        this.buildEntrance();
      }, this.host.nativeElement);

      this.bindTilt();
      this.watchVisibility();
    });
  }

  /* ------------------------------------------------------------------
     ENTRANCE — badge, heading, subtitle, then the cards
     ------------------------------------------------------------------ */
  private buildEntrance(): void {
    const tl = gsap.timeline({
      defaults: { ease: 'power3.out' },
      scrollTrigger: {
        trigger: this.head!.nativeElement,
        start: 'top 84%',
        once: true,
      },
    });

    tl.fromTo('.tech__badge', ...rsFromTo({ opacity: 0, y: 18, duration: 0.6 }))
      .fromTo('.tech__title', ...rsFromTo({ opacity: 0, y: 26, duration: 0.8 }), '-=0.38')
      .fromTo('.tech__divider', ...rsFromTo({ opacity: 0, scaleX: 0, duration: 0.6 }), '-=0.5')
      .fromTo('.tech__lede', ...rsFromTo({ opacity: 0, y: 16, duration: 0.6 }), '-=0.45');

    const cards = (this.cardEls?.toArray() ?? []).map((r) => r.nativeElement);
    if (cards.length) {
      gsap.fromTo(cards, ...rsFromTo({
        opacity: 0,
        y: 44,
        scale: 0.94,
        duration: 0.8,
        ease: 'power3.out',
        stagger: 0.06,
        scrollTrigger: {
          trigger: this.grid!.nativeElement,
          start: 'top 88%',
          once: true,
        },
        // hand each card to its idle float once it has landed
        onComplete: () => this.startFloat(cards),
      }));
    }

    gsap.fromTo('.tech__banner', ...rsFromTo({
      opacity: 0,
      y: 30,
      duration: 0.8,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: '.tech__banner',
        start: 'top 92%',
        once: true,
      },
    }));
  }

  /* ------------------------------------------------------------------
     FLOAT — each card drifts on its own clock.
     Runs on .tech__inner (an inner wrapper) so the tilt on the card
     itself never fights this tween for the transform.
     ------------------------------------------------------------------ */
  private startFloat(cards: HTMLElement[]): void {
    cards.forEach((c, i) => {
      const inner = c.querySelector('.tech__lift');
      if (!inner) return;
      this.loops.push(
        gsap.to(inner, {
          y: -6,
          duration: 4 + (i % 5) * 0.45,
          ease: 'sine.inOut',
          yoyo: true,
          repeat: -1,
          delay: (i % 7) * 0.32,
        })
      );
    });
  }

  /* ------------------------------------------------------------------
     TILT — max 6 degrees, from the pointer's position in the card
     ------------------------------------------------------------------ */
  private bindTilt(): void {
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!fine) return;

    const MAX = 6;

    (this.cardEls?.toArray() ?? []).forEach(({ nativeElement: el }) => {
      const rx = gsap.quickTo(el, 'rotateX', { duration: 0.5, ease: 'power3.out' });
      const ry = gsap.quickTo(el, 'rotateY', { duration: 0.5, ease: 'power3.out' });

      let queued = false;
      let nx = 0;
      let ny = 0;

      const apply = () => {
        queued = false;
        rx(-ny * MAX);
        ry(nx * MAX);
      };

      const onMove = (e: MouseEvent) => {
        const r = el.getBoundingClientRect();
        nx = (e.clientX - r.left) / r.width - 0.5;
        ny = (e.clientY - r.top) / r.height - 0.5;
        // sheen follows the cursor too
        el.style.setProperty('--mx', `${(nx + 0.5) * 100}%`);
        el.style.setProperty('--my', `${(ny + 0.5) * 100}%`);
        if (queued) return;
        queued = true;
        requestAnimationFrame(apply);
      };

      const onLeave = () => {
        nx = 0;
        ny = 0;
        if (queued) return;
        queued = true;
        requestAnimationFrame(apply);
      };

      el.addEventListener('mousemove', onMove, { passive: true });
      el.addEventListener('mouseleave', onLeave, { passive: true });
      this.cleanups.push(() => {
        el.removeEventListener('mousemove', onMove);
        el.removeEventListener('mouseleave', onLeave);
      });
    });
  }

  /* ------------------------------------------------------------------
     VISIBILITY — stop every animation once the section is off screen.
     CSS loops are paused by a class; GSAP tweens are paused directly.
     ------------------------------------------------------------------ */
  private watchVisibility(): void {
    const el = this.root?.nativeElement;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    this.io = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        // CSS keyframes stop via the class; GSAP loops are paused directly
        el.classList.toggle('is-paused', !visible);
        this.loops.forEach((t) => t.paused(!visible));
      },
      { threshold: 0 }
    );

    this.io.observe(el);
    this.cleanups.push(() => this.io?.disconnect());
  }

  ngOnDestroy(): void {
    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];
    this.loops = [];
    this.io?.disconnect();
    this.ctx?.revert();
  }
}
